import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CustomerCreateRequest,
  CustomerListQuery,
  CustomerListResponse,
  CustomerResponse,
  CustomerUpdateRequest,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: (q: Partial<CustomerListQuery>) => ['customers', 'list', q] as const,
  one: (id: string) => ['customers', 'one', id] as const,
};

function toQueryString(q: Partial<CustomerListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.search) params.set('search', q.search);
  if (q.country) params.set('country', q.country);
  if (q.active !== undefined) params.set('active', String(q.active));
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useCustomers(query: Partial<CustomerListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () => api<CustomerListResponse>(`/api/customers${toQueryString(query)}`),
    staleTime: 10_000,
  });
}

export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['customers', 'one', 'NIL'],
    queryFn: () => api<CustomerResponse>(`/api/customers/${id}`),
    enabled: !!id,
  });
}

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CustomerCreateRequest) =>
      api<CustomerResponse>('/api/customers', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers', 'list'] });
    },
  });
}

export function useUpdateCustomer(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CustomerUpdateRequest) =>
      api<CustomerResponse>(`/api/customers/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['customers', 'list'] });
    },
  });
}

export function useDeleteCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/customers/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['customers', 'list'] });
    },
  });
}
